export type ObjectCoverRect = {
  drawX: number;
  drawY: number;
  drawW: number;
  drawH: number;
};

/**
 * Computes the draw rect for an image rendered with CSS `object-fit: cover` inside
 * a destination box. The image is scaled uniformly until both axes fill the box;
 * overflow is centered and clipped.
 */
export const computeObjectCoverRect = (
  srcWidth: number,
  srcHeight: number,
  destX: number,
  destY: number,
  destWidth: number,
  destHeight: number,
): ObjectCoverRect => {
  const scale = Math.max(destWidth / srcWidth, destHeight / srcHeight);
  const drawW = srcWidth * scale;
  const drawH = srcHeight * scale;

  return {
    drawH,
    drawW,
    drawX: destX + (destWidth - drawW) / 2,
    drawY: destY + (destHeight - drawH) / 2,
  };
};

const getSourceDimensions = (source: CanvasImageSource): { height: number; width: number } => {
  if (source instanceof HTMLImageElement) {
    return { height: source.naturalHeight, width: source.naturalWidth };
  }

  if (source instanceof HTMLCanvasElement || source instanceof HTMLVideoElement) {
    return { height: source.height, width: source.width };
  }

  if (source instanceof ImageBitmap) {
    return { height: source.height, width: source.width };
  }

  return { height: 0, width: 0 };
};

/** Draws `source` into the destination box using CSS `object-fit: cover` semantics. */
export const drawImageObjectCover = (
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  destX: number,
  destY: number,
  destWidth: number,
  destHeight: number,
): void => {
  const { height: srcHeight, width: srcWidth } = getSourceDimensions(source);

  if (srcWidth <= 0 || srcHeight <= 0 || destWidth <= 0 || destHeight <= 0) {
    return;
  }

  const { drawH, drawW, drawX, drawY } = computeObjectCoverRect(
    srcWidth,
    srcHeight,
    destX,
    destY,
    destWidth,
    destHeight,
  );

  ctx.save();
  ctx.beginPath();
  ctx.rect(destX, destY, destWidth, destHeight);
  ctx.clip();
  ctx.drawImage(source, drawX, drawY, drawW, drawH);
  ctx.restore();
};
